import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { NgxAuthService } from '@go-modules/services/auth/auth.service';
import { emailValidator } from '@go-modules/form-validator/email.validator/email.validator';
import { ResetPasswordSources } from '@go-modules/enums/';
import { BehaviorSubject, EMPTY, Subject, catchError, combineLatest, finalize, map, takeUntil } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AuthTemplateModule } from '@go-modules/components/auth-template/auth-template.module';
import { GoMaterialModule } from '@go-modules/go-material.module';
import { CommonModule } from '@angular/common';
import { ROUTE_NAMES, RouterService } from '@go-modules/services/router/router.service';
import { Router } from '@angular/router';

@Component({
	selector: 'ngx-forgot-password',
	imports: [
		CommonModule,
		FormsModule,
		ReactiveFormsModule,
		TranslateModule,
		GoMaterialModule,
		AuthTemplateModule
	],
	templateUrl: './forgot-password.component.html',
	styleUrls: ['./forgot-password.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class ForgotPasswordComponent  implements OnInit, OnDestroy {
	public sessionId: string;
	public routeSource: ResetPasswordSources = ResetPasswordSources.DASHBOARD;

	public form: UntypedFormGroup;
	public requestSuccess$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	public requestError$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	public loading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	public isResetLocked$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	public resetLockDuration$: BehaviorSubject<number> = new BehaviorSubject<number>(0);
	public componentDestroyed$$ = new Subject();
	public disableButton$ = combineLatest([
		this.loading$,
		this.isResetLocked$,
		this.requestError$
	]).pipe(
		takeUntil(this.componentDestroyed$$),
		map(([loading, isResetLocked, requestError]) =>
			loading || isResetLocked || requestError
		)
	);
	private lockIntervalId;

	constructor (
		public router: Router,
		private routerService: RouterService,
		private authService: NgxAuthService,
		private translate: TranslateService
	) {}

	public ngOnInit () {
		this.form = new UntypedFormGroup({
			email: new UntypedFormControl(null, [Validators.required, emailValidator()])
		});

		this.form.get('email').valueChanges
			.pipe(takeUntil(this.componentDestroyed$$))
			.subscribe(() => this.requestError$.next(false));

		if (this.routerService.getCurrentData().isVideoShare) {
			this.sessionId = this.routerService.getCurrentQueryParams().session;
			this.routeSource = ResetPasswordSources.GUEST_REVIEW;
		}
	}

	public ngOnDestroy (): void {
		clearInterval(this.lockIntervalId);
		this.componentDestroyed$$.next(true);
		this.componentDestroyed$$.complete();
	}

	public submit (form) {
		if (form.invalid) {
			return;
		}

		this.loading$.next(true);
		this.authService.requestPassword(form.get('email').value, this.sessionId, this.routeSource)
			.pipe(
				catchError((response: HttpErrorResponse) => {
					if (response.status === 423) {
						this.isResetLocked$.next(true);
						let timeLeft = response.error.remainingResetLockSeconds;

						this.lockIntervalId = setInterval(() => {
							timeLeft -= 1;
							this.resetLockDuration$.next(timeLeft);

							if (timeLeft <= 0) {
								clearInterval(this.lockIntervalId);
								this.isResetLocked$.next(false);
							}
						}, 1000);
					} else {
						this.requestError$.next(true);
					}
					return EMPTY;
				}),
				finalize(() => {
					this.loading$.next(false);
				})
			)
			.subscribe(() => {
				this.requestSuccess$.next(true);
			});
	}

	public navigateBack () {
		if (this.routerService.getCurrentData().isVideoShare) {
			this.router.navigate(['/login'], { queryParamsHandling: 'preserve' });
		} else {
			this.routerService.navigateTo(ROUTE_NAMES.AUTH_LOGIN, {}, { onSameUrlNavigation: 'reload' });
		}
	}

	public disableRequestPasswordButton () {
		return this.form.controls.email.hasError('required') || this.form.controls.email.hasError('email');
	}

	public getResendDurationMessage (duration) {
		return this.translate.instant('forgot-password-request-resend_in-message', {
			duration
		});
	}
}
